<template>
  <section class="hero" data-cy="membership-health-hero">
    <header class="hero__head">
      <span class="hero__eyebrow">Active Members</span>
      <Tooltip placement="top" :width="280">
        <template #trigger>
          <i
            class="fas fa-info-circle hero__info"
            aria-label="More information"
          ></i>
        </template>
        <template #tooltip>
          <span class="hero__tooltip-text">{{ TOOLTIP }}</span>
        </template>
      </Tooltip>
    </header>

    <div class="hero__value-row">
      <Skeleton
        v-if="loading"
        width="9rem"
        height="3.5rem"
        :rounded="false"
        data-cy="membership-health-hero-skeleton"
      />
      <span v-else class="hero__value" data-cy="membership-health-hero-value">
        {{ activeMembersValue }}
      </span>

      <figure
        v-if="sparkPoints"
        class="hero__sparkline-wrap"
        data-cy="membership-health-hero-sparkline"
      >
        <svg
          viewBox="0 0 140 48"
          preserveAspectRatio="none"
          class="hero__sparkline"
          aria-hidden="true"
          role="presentation"
        >
          <polyline
            :points="sparkPoints"
            fill="none"
            stroke="#0F3B95"
            stroke-width="1.75"
            stroke-linejoin="round"
            stroke-linecap="round"
            stroke-opacity="0.75"
          />
        </svg>
        <figcaption class="hero__sparkline-caption">
          <Tooltip v-if="showDelta" placement="top" :width="240">
            <template #trigger>
              <span
                class="hero__delta-pill"
                :class="`hero__delta-pill--${deltaTone}`"
                data-cy="hero-delta-active-members"
              >
                {{ deltaLabel }} over 12 months
              </span>
            </template>
            <template #tooltip>
              <span class="hero__tooltip-text">{{ deltaTooltipText }}</span>
            </template>
          </Tooltip>
          <span v-else class="hero__sparkline-fallback">12-month trend</span>
        </figcaption>
      </figure>
    </div>

    <div class="hero__deltas-wrap">
      <span class="hero__deltas-label">Last 30 Days</span>
      <ul class="hero__deltas" aria-label="Last 30 days flow">
        <li class="hero__delta">
          <component
            :is="addedHref ? 'a' : 'span'"
            :href="addedHref"
            class="hero__delta-link"
            :class="{ 'hero__delta-link--linked': addedHref }"
            data-cy="hero-delta-added"
          >
            <span class="hero__delta-value hero__delta-value--positive">{{
              addedValue
            }}</span>
            <span class="hero__delta-label">Added</span>
          </component>
        </li>
        <li class="hero__delta">
          <component
            :is="lostHref ? 'a' : 'span'"
            :href="lostHref"
            class="hero__delta-link"
            :class="{ 'hero__delta-link--linked': lostHref }"
            data-cy="hero-delta-lost"
          >
            <span class="hero__delta-value hero__delta-value--negative">{{
              lostValue
            }}</span>
            <span class="hero__delta-label">Lost</span>
          </component>
        </li>
        <li class="hero__delta">
          <span class="hero__delta-link" data-cy="hero-delta-net">
            <span
              class="hero__delta-value"
              :class="`hero__delta-value--${netTone}`"
              >{{ netValue }}</span
            >
            <span class="hero__delta-label">Net</span>
          </span>
        </li>
        <li class="hero__delta">
          <span class="hero__delta-link" data-cy="hero-delta-churn">
            <span class="hero__delta-value">{{ churnValue }}</span>
            <span class="hero__delta-label">Churn</span>
          </span>
        </li>
      </ul>
    </div>
  </section>
</template>

<script setup lang="ts">
import moment from "moment";
import { computed } from "vue";

import Tooltip from "@/components/Tooltip.vue";
import Skeleton from "./Skeleton.vue";
import { useMembershipDashboard } from "./useMembershipDashboard";

const TOOLTIP =
  "Active members in the program right now, with the last 30 days of additions, losses, net change, and churn rate.";

const numberFormatter = new Intl.NumberFormat("en-US");

const { summary, loading, growthChart, membersReportHref } =
  useMembershipDashboard();

function trailing30DayRange(): string {
  const today = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 30);
  const fmt = (d: Date) => moment(d).format("MM/DD/YYYY");
  return `${fmt(start)}-${fmt(today)}`;
}

const activeMembersValue = computed(() => {
  const count = summary.value?.active_members;
  if (count === null || count === undefined) return "—";
  return numberFormatter.format(count);
});

const addedValue = computed(() => {
  const v = summary.value?.new_members ?? 0;
  return `+${numberFormatter.format(v)}`;
});
const lostValue = computed(() => {
  const v = summary.value?.lost_members ?? 0;
  return v === 0 ? "0" : `−${numberFormatter.format(v)}`;
});
const net = computed(() => {
  const newM = summary.value?.new_members ?? 0;
  const lostM = summary.value?.lost_members ?? 0;
  return newM - lostM;
});
const netValue = computed(() => {
  const n = net.value;
  if (n > 0) return `+${numberFormatter.format(n)}`;
  if (n < 0) return numberFormatter.format(n); // already includes the minus
  return "0";
});
const netTone = computed<"positive" | "negative" | "neutral">(() => {
  const n = net.value;
  if (n > 0) return "positive";
  if (n < 0) return "negative";
  return "neutral";
});
const churnValue = computed(() => {
  const rate = summary.value?.churn_rate;
  if (rate === null || rate === undefined) return "—";
  return `${Number(rate).toFixed(1)}%`;
});

// 12-month sparkline of active_members. Reuses the growth-chart Vuex slice
// (already fetched by MembershipGrowthChart on mount) so no new backend hop.
// SVG path normalizes y-values to the data range with a small vertical pad
// so the line never touches the box edges.
const SPARK_WIDTH = 140;
const SPARK_HEIGHT = 48;
const SPARK_VPAD = 4;

const sparkPoints = computed<string>(() => {
  const buckets = growthChart.value;
  if (!buckets || buckets.length < 2) return "";
  const values = buckets.map((b) => b.active_members);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const innerHeight = SPARK_HEIGHT - SPARK_VPAD * 2;
  return values
    .map((v, i) => {
      const x = (i / (values.length - 1)) * SPARK_WIDTH;
      const y = SPARK_VPAD + (innerHeight - ((v - min) / range) * innerHeight);
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");
});

const addedHref = computed(() =>
  membersReportHref(
    `/reports/members?starts_on=${trailing30DayRange()}&first_ever_membership=true`
  )
);
const lostHref = computed(() =>
  membersReportHref(
    `/reports/members?customer_ended_within=${trailing30DayRange()}&no_current_membership_in_scope=true`
  )
);

// Trailing-12-month change sourced from the growth-chart slice so the pill
// matches the sparkline's window. Comparing current vs previous month would
// flip negative every month-start (current bucket is a partial-month forward
// projection) — comparing against the oldest bucket smooths that noise out.
const priorBucket = computed(() => {
  const buckets = growthChart.value;
  if (!buckets || buckets.length < 2) return null;
  return buckets[0] ?? null;
});

const currentBucket = computed(() => {
  const buckets = growthChart.value;
  if (!buckets || buckets.length === 0) return null;
  return buckets.at(-1) ?? null;
});

const delta = computed<number>(() => {
  const prior = priorBucket.value?.active_members ?? 0;
  const current = currentBucket.value?.active_members ?? 0;
  return current - prior;
});

const deltaLabel = computed<string>(() => {
  const d = delta.value;
  if (d > 0) return `+${numberFormatter.format(d)} ↑`;
  if (d < 0) return `−${numberFormatter.format(Math.abs(d))} ↓`;
  return "0 →";
});

const deltaTone = computed<"positive" | "negative" | "neutral">(() => {
  const d = delta.value;
  if (d > 0) return "positive";
  if (d < 0) return "negative";
  return "neutral";
});

const priorMonthLabel = computed<string>(() => {
  const month = priorBucket.value?.month;
  if (!month) return "";
  return moment(month, "YYYY-MM").format("MMMM YYYY");
});

const deltaTooltipText = computed<string>(() => {
  const prior = priorBucket.value?.active_members ?? 0;
  return `Was ${numberFormatter.format(prior)} in ${priorMonthLabel.value}`;
});

const showDelta = computed<boolean>(() => {
  if (loading.value) return false;
  if (!priorBucket.value) return false;
  return priorBucket.value.active_members > 0;
});
</script>

<style lang="scss" scoped>
.hero {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  background-color: #fff;
  border: 1px solid var(--medium-color, #e5e7eb);
  border-radius: 6px;
  padding: 1.5rem 1.75rem;
}
.hero__head {
  display: flex;
  align-items: center;
  gap: 0.375rem;
}
.hero__eyebrow {
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #4b5563;
}
.hero__info {
  font-size: 0.8125rem;
  color: #9ca3af;
  cursor: help;
  transition: color 120ms ease;
  &:hover {
    color: #4b5563;
  }
}
.hero__tooltip-text {
  font-size: 0.8125rem;
  line-height: 1.4;
}
.hero__value-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.5rem;
  flex-wrap: wrap;
}
.hero__delta-pill {
  font-size: 0.75rem;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  font-feature-settings: "tnum";
  cursor: help;
  white-space: nowrap;
  letter-spacing: 0.01em;
}
.hero__delta-pill--positive {
  color: #047857;
}
.hero__delta-pill--negative {
  color: #b91c1c;
}
.hero__delta-pill--neutral {
  color: #6b7280;
}
.hero__sparkline-fallback {
  font-size: 0.6875rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #6b7280;
}
.hero__sparkline-wrap {
  margin: 0;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0.25rem;
  flex-shrink: 0;
}
.hero__sparkline {
  width: 140px;
  height: 48px;
  display: block;
}
.hero__sparkline-caption {
  text-align: right;
  line-height: 1.1;
}
.hero__value {
  font-size: 3rem;
  font-weight: 600;
  line-height: 1;
  color: #111827;
  font-variant-numeric: tabular-nums;
  font-feature-settings: "tnum";
  letter-spacing: -0.01em;
}
.hero__deltas-wrap {
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
  padding-top: 1rem;
  border-top: 1px solid var(--medium-color, #e5e7eb);
}
.hero__deltas-label {
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #6b7280;
}
.hero__deltas {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem 2.5rem;
}
.hero__delta {
  display: flex;
}
.hero__delta-link {
  display: inline-flex;
  align-items: baseline;
  gap: 0.5rem;
  text-decoration: none;
  color: inherit;
}
.hero__delta-link--linked {
  border-bottom: 1px dotted transparent;
  transition: border-color 120ms ease;
  &:hover {
    border-bottom-color: #6b7280;
  }
}
.hero__delta-value {
  font-size: 1.375rem;
  font-weight: 600;
  color: #111827;
  font-variant-numeric: tabular-nums;
  font-feature-settings: "tnum";
}
.hero__delta-value--positive {
  color: #047857;
}
.hero__delta-value--negative {
  color: #b91c1c;
}
.hero__delta-value--neutral {
  color: #111827;
}
.hero__delta-label {
  font-size: 0.75rem;
  color: #6b7280;
  letter-spacing: 0.02em;
}
</style>
